import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";

export default function PoliticaDeCookieuri() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    document.title = "Politica de Cookie-uri — NIS2PASS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Politica de cookie-uri NIS2PASS - tipuri și gestionare."
      );
    }
  }, []);

  return (
    <div className="lp-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --c-navy: #0F172A;
          --c-navy-dark: #1E293B;
          --c-navy-darkest: #0B1220;
          --c-blue: #2563EB;
          --c-blue-dark: #1D4ED8;
          --c-blue-light: #60A5FA;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-bg-light: #F8FAFC;
          --c-bg-page: #F7FAFF;
          --c-border-light: #E2E8F0;
          --c-border-mid: #CBD5E1;
          --c-text-body: #475569;
          --c-text-muted: #64748B;
          --c-text-dim: #94A3B8;
          --c-text-faint: #CBD5E1;
          --c-white: #FFF;
          --c-black: #111;
        }

        .lp-page { background: var(--c-bg-light); color: var(--c-navy); font-family: 'Sora', system-ui, sans-serif; min-height: 100vh; display: flex; flex-direction: column; }
        .lp-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .lp-page a { text-decoration: none; color: inherit; }

        /* NAV */
        .lp-nav { width: 100%; background: var(--c-navy); padding: 28px 0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lp-nav-inner { max-width: 1200px; width: 100%; padding: 0 24px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
        .lp-nav-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; text-decoration: none; }
        .lp-nav-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .lp-nav-brand { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-white); }
        .lp-nav-links { display: flex; align-items: center; gap: 24px; }
        .lp-nav-link { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .lp-nav-link:hover { color: var(--c-white); }
        .lp-nav-link.active { color: var(--c-blue-light); }
        .lp-nav-btns { display: flex; align-items: center; gap: 12px; }
        .lp-nav-ghost { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); background: transparent; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms; }
        .lp-nav-ghost:hover { border-color: var(--c-blue-light); color: var(--c-white); background: rgba(96,165,250,0.08); }
        .lp-nav-cta { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-black); background: var(--c-orange); border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms; }
        .lp-nav-cta:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* MAIN CONTENT */
        .lp-main { flex: 1; max-width: 1120px; width: 100%; margin: 0 auto; padding: 80px 24px; }

        /* HEADER */
        .lp-label { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-blue); letter-spacing: 1.4px; text-transform: uppercase; margin-bottom: 12px; }
        .lp-title { font-family: 'Sora', sans-serif; font-size: 38px; font-weight: 700; color: var(--c-navy); letter-spacing: -1.2px; line-height: 1.08; margin-bottom: 32px; }

        /* DOCUMENT CARD */
        .lp-card { background: var(--c-white); border: 1px solid var(--c-border-light); border-radius: 8px; padding: 32px 36px; position: relative; }

        .lp-doc-title { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; color: var(--c-navy); margin-bottom: 8px; }
        .lp-doc-date { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 4px; }
        .lp-doc-company { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 16px; }
        .lp-doc-divider { border: none; height: 1px; background: var(--c-border-light); margin: 16px 0 24px; }
        .lp-doc-heading { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--c-navy); margin: 24px 0 8px; }
        .lp-doc-body { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin-bottom: 8px; }
        .lp-doc-body strong { color: var(--c-navy); font-weight: 700; }
        .lp-doc-list { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin: 4px 0 12px 20px; list-style: disc; }
        .lp-doc-list li { margin-bottom: 4px; }
        .lp-doc-list li strong { color: var(--c-navy); font-weight: 700; }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .lp-main { padding: 48px 24px; }
          .lp-title { font-size: 28px; letter-spacing: -0.8px; margin-bottom: 24px; }
          .lp-card { padding: 24px 20px; }
          .lp-doc-title { font-size: 20px; }
          .lp-nav-links, .lp-nav-btns { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-logo">
            <span className="lp-nav-logo-mark" />
            <span className="lp-nav-brand">NIS2PASS</span>
          </a>
          <div className="lp-nav-links">
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/")}
            >
              Platforma
            </button>
            <button
              type="button"
              className="lp-nav-link active"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="lp-nav-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/logout")}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Intra in cont
                </button>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/auth")}
                >
                  Creeaza cont gratuit
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="lp-main">
        <p className="lp-label">DOCUMENTE LEGALE NIS2PASS</p>
        <h1 className="lp-title">Politica de Cookie-uri</h1>
        <div className="lp-card">
          <h2 className="lp-doc-title">
            Politica de Cookie-uri — Versiunea 1.0
          </h2>
          <p className="lp-doc-date">22 Septembrie 2025</p>
          <p className="lp-doc-company">
            Compliance Made Easy SRL · CUI: 48993456 · J2023019988400
          </p>
          <hr className="lp-doc-divider" />

          <h3 className="lp-doc-heading">1. Ce Sunt Cookie-urile</h3>
          <p className="lp-doc-body">
            Cookie-urile sunt fișiere text de mici dimensiuni stocate pe
            dispozitivul dumneavoastră atunci când vizitați un site web. Acestea
            permit site-ului să rețină informații despre vizita dumneavoastră.
          </p>

          <h3 className="lp-doc-heading">2. Tipuri de Cookie-uri Utilizate</h3>
          <ul className="lp-doc-list">
            <li>
              <strong>Cookie-uri strict necesare:</strong> Esențiale pentru
              funcționarea platformei (autentificare, sesiune, securitate). Nu
              necesită consimțământ.
            </li>
            <li>
              <strong>Cookie-uri de performanță și analiză:</strong> Utilizate
              pentru a înțelege modul în care vizitatorii interacționează cu
              platforma (ex: PostHog). Necesită consimțământ.
            </li>
            <li>
              <strong>Cookie-uri funcționale:</strong> Permit personalizarea
              experienței (preferințe de limbă, setări salvate). Necesită
              consimțământ.
            </li>
          </ul>

          <h3 className="lp-doc-heading">3. Cookie-uri Terțe</h3>
          <p className="lp-doc-body">
            Platforma poate utiliza servicii terțe care setează propriile
            cookie-uri:
          </p>
          <ul className="lp-doc-list">
            <li>
              <strong>PostHog:</strong> Analiză de utilizare și comportament.
            </li>
            <li>
              <strong>Supabase:</strong> Autentificare și gestionarea
              sesiunilor.
            </li>
            <li>
              <strong>Stripe:</strong> Procesarea plăților (numai pe paginile de
              plată).
            </li>
          </ul>

          <h3 className="lp-doc-heading">4. Gestionarea Cookie-urilor</h3>
          <p className="lp-doc-body">
            Puteți gestiona preferințele de cookie-uri prin:
          </p>
          <ul className="lp-doc-list">
            <li>
              Bannerul de consimțământ afișat la prima vizită pe platformă.
            </li>
            <li>Setările browserului dumneavoastră.</li>
            <li>
              Dezactivarea cookie-urilor poate afecta funcționalitatea
              platformei.
            </li>
          </ul>

          <h3 className="lp-doc-heading">
            5. Modificări ale Politicii de Cookie-uri
          </h3>
          <p className="lp-doc-body">
            Ne rezervăm dreptul de a actualiza această politică. Modificările
            vor fi comunicate prin actualizarea datei de revizuire.
          </p>
        </div>
      </main>

      {/* FOOTER */}
      <SharedFooter />
    </div>
  );
}
